import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert,
  AlertTitle,
  IconButton,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import api from '@/services/api';

interface OptimizationFormProps {
  testMode?: boolean;
}

const OptimizationForm: React.FC<OptimizationFormProps> = ({ testMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [wildTypeMeltingTemp, setWildTypeMeltingTemp] = useState<string>('');
  const [targetMeltingTemp, setTargetMeltingTemp] = useState<string>('');
  const [jobName, setJobName] = useState<string>('');
  const [optimizationType, setOptimizationType] = useState<string>('manual');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Check if test mode is enabled from location state
  const isTestMode = testMode || location.state?.testMode;
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      // Only accept .pdb files
      if (file.name.toLowerCase().endsWith('.pdb')) {
        setSelectedFile(file);
        setError(null);
      } else {
        setSelectedFile(null);
        setError('Please upload a valid .pdb file');
      }
    }
  };
  
  const handleSubmit = async () => {
    if (!selectedFile) {
      setError('Please upload a PDB file');
      return;
    }
    
    if (!jobName.trim()) {
      setError('Please enter a name for this optimization job');
      return;
    }
    
    try {
      setUploading(true);
      setError(null);
      
      if (isTestMode) {
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 30000));
        // Navigate to results page with test mode enabled
        navigate('/optimize/test-123', { state: { testMode: true } });
        return;
      }
      
      // Create form data for the file and temperature values
      const formData = new FormData();
      formData.append('pdb_file', selectedFile);
      formData.append('jobname', jobName.trim());
      formData.append('wild_type_melting_temp', wildTypeMeltingTemp);
      formData.append('target_melting_temp', targetMeltingTemp);
      formData.append('optimization_type', optimizationType);
      
      // Send the data to the backend
      const response = await api.post('/api/optimize/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Navigate to the results page
      navigate(`/optimize/${response.data.id}`);
      
    } catch (error) {
      console.error('Error submitting optimization:', error);
      setError('Failed to submit optimization. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box mb={4} mt={3}>
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton 
            sx={{ mr: 2 }}
            onClick={() => navigate('/jobs')}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Protein Optimization
          </Typography>
        </Box>

        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" paragraph>
            Upload a PDB file of your protein to optimize.
          </Typography>
          
          {isTestMode && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Test Mode</AlertTitle>
              You are in test mode. After submitting, you will see mock results after a 30-second delay.
            </Alert>
          )}
          
          <Box 
            border={1} 
            borderRadius={1} 
            borderColor="divider" 
            p={3} 
            textAlign="center"
            mb={3}
          >
            <input
              id="pdb-upload"
              type="file"
              accept=".pdb"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <label htmlFor="pdb-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<AddIcon />}
              >
                Select PDB File
              </Button>
            </label>
            
            {selectedFile && (
              <Box mt={2}>
                <Typography variant="body2" color="primary">
                  Selected: {selectedFile.name}
                </Typography>
              </Box>
            )}
          </Box>
          
          <TextField
            fullWidth
            required
            label="Job Name"
            variant="outlined"
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            margin="normal"
            helperText="Enter a descriptive name for this optimization job"
          />
          
          <TextField
            fullWidth
            label="If known, what's the wild type (WT) melting temperature? (°C)"
            variant="outlined"
            value={wildTypeMeltingTemp}
            onChange={(e) => setWildTypeMeltingTemp(e.target.value)}
            margin="normal"
            type="number"
            InputProps={{
              endAdornment: <Typography variant="body2">°C</Typography>
            }}
          />
          
          <TextField
            fullWidth
            label="Is there a target melting temp for the final variant? (°C)"
            variant="outlined"
            value={targetMeltingTemp}
            onChange={(e) => setTargetMeltingTemp(e.target.value)}
            margin="normal"
            type="number"
            InputProps={{
              endAdornment: <Typography variant="body2">°C</Typography>
            }}
          />
          
          <FormControl component="fieldset" sx={{ mt: 3 }}>
            <FormLabel component="legend">Optimization Type</FormLabel>
            <RadioGroup
              row
              name="optimization-type"
              value={optimizationType}
              onChange={(e) => setOptimizationType(e.target.value)}
            >
              <FormControlLabel 
                value="manual" 
                control={<Radio />} 
                label="Manual Expert Review" 
              />
              <FormControlLabel 
                value="ligandmpnn" 
                control={<Radio />} 
                label="LigandMPNN Zero-Shot" 
              />
            </RadioGroup>
            {optimizationType === 'ligandmpnn' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                LigandMPNN will automatically generate optimized protein sequences using a machine learning approach without requiring lab data.
              </Typography>
            )}
            {optimizationType === 'manual' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Your PDB file will be reviewed by our protein engineering experts.
              </Typography>
            )}
          </FormControl>

          {error && (
            <Box mt={2}>
              <Alert severity="error">
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
            </Box>
          )}
          
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => navigate('/jobs')}
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              color="primary"
              disabled={!selectedFile || uploading}
            >
              {uploading ? <CircularProgress size={24} /> : 'Submit'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default OptimizationForm; 